import { useTranslation } from "react-i18next";
import { Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { formatDateTime, formatRelativeTime } from "@/lib/date";
import {
  auditKindIcon,
  decisionColor,
  DECISION_BADGE_CLASSES,
  formatAuditTitle,
} from "@/lib/auditUtils";
import type { AuditEventItem } from "@/hooks/useAuditEvents";

export interface AuditTimelineProps {
  events: AuditEventItem[];
  total: number;
  earliestEventAt: string | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

function AuditSkeleton() {
  return (
    <div data-testid="audit-skeleton" className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-lg border bg-muted/50"
        />
      ))}
    </div>
  );
}

function shortSha(sha: string | null): string | null {
  if (!sha) return null;
  return sha.slice(0, 7);
}

function TraceabilityChips({ event }: { event: AuditEventItem }) {
  const chips: { key: string; value: string }[] = [];
  if (event.work_item_ref) {
    chips.push({ key: "work_item_ref", value: event.work_item_ref });
  }
  if (event.artifact_ref) {
    chips.push({ key: "artifact_ref", value: event.artifact_ref });
  }
  if (event.branch) {
    chips.push({ key: "branch", value: event.branch });
  }
  const sha = shortSha(event.commit_sha);
  if (sha) {
    chips.push({ key: "commit_sha", value: sha });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
        >
          {chip.value}
        </span>
      ))}
    </div>
  );
}

function MetadataLine({ event }: { event: AuditEventItem }) {
  const parts = [
    event.pipeline_name,
    event.workflow_point,
    event.run_id,
    event.actor_kind,
  ].filter((p): p is string => Boolean(p));

  if (parts.length === 0) return null;

  return (
    <p className="text-xs text-muted-foreground">{parts.join(" · ")}</p>
  );
}

function TimelineItem({ event, isLast }: { event: AuditEventItem; isLast: boolean }) {
  const { t } = useTranslation();
  const Icon = auditKindIcon(event.event_kind);
  const title = formatAuditTitle(event, t);
  const color = decisionColor(event.decision);

  return (
    <li className="relative flex gap-3 pb-6 last:pb-0">
      {!isLast && <div className="absolute left-3.5 top-8 bottom-0 w-px bg-border" />}
      <div className="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-card">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 space-y-1.5 pt-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="audit-item-title" className="text-sm font-medium">
            {title}
          </span>
          {event.decision !== null && (
            <Badge
              data-testid="audit-decision-badge"
              variant="outline"
              className={cn("font-mono", DECISION_BADGE_CLASSES[color])}
            >
              {event.decision}
            </Badge>
          )}
        </div>
        <TraceabilityChips event={event} />
        <MetadataLine event={event} />
        <time
          dateTime={event.occurred_at}
          title={formatDateTime(event.occurred_at)}
          className="block text-xs text-muted-foreground"
        >
          {formatRelativeTime(event.occurred_at)}
        </time>
      </div>
    </li>
  );
}

export function AuditTimeline({
  events,
  total,
  earliestEventAt,
  loading,
  error,
  onRetry,
}: AuditTimelineProps) {
  const { t } = useTranslation();

  if (loading) return <AuditSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12">
        <p className="text-sm text-destructive">{t("audit.loadError")}</p>
        <button
          onClick={onRetry}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t("audit.retry")}
        </button>
      </div>
    );
  }

  if (events.length === 0) {
    // T7: two distinct honest empty states — "never recorded" vs "filters
    // excluded everything" — driven by the server's earliest_event_at
    // contract (schemas/audit.py), never conflated.
    if (earliestEventAt === null) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
          <Shield className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm font-medium">{t("audit.empty")}</p>
          <p className="text-xs text-muted-foreground">
            {t("audit.emptyDetail")}
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <p className="text-sm font-medium">{t("audit.noMatch")}</p>
      </div>
    );
  }

  return (
    <div>
      {total > events.length && (
        <p className="mb-3 text-xs text-muted-foreground">
          {t("audit.truncated", { shown: events.length, total })}
        </p>
      )}
      <ol className="space-y-0">
        {events.map((event, i) => (
          <TimelineItem key={event.id} event={event} isLast={i === events.length - 1} />
        ))}
      </ol>
    </div>
  );
}
